import * as React from "react";
import {FC, useMemo, useState} from "react";
import {useParams} from "react-router";
import {LinearProgress} from "@mui/material";
import {useRecoilValue} from "recoil";
import Page from "../../layout/Page";
import PageHeader from "../../layout/PageHeader";
import PageContent from "../../layout/PageContent";
import {BodyText} from "../../layout/Typography";
import {MessagesList} from "../admin/MessagesList";
import {CurrentUserIsAdmin} from "../../atoms/CurrentUser";
import {ParamBrandHouse} from "../../Parameters";
import {
   EnumIntegrationInstanceRefType,
   FilterFindManyIntegrationInstanceInput,
   useBrandMessagesInstancesQuery,
   useBrandMessagesShopsQuery
} from "../../Queries";

export const BrandMessagesPagePath = (brandHouseId: string) => {
   return "/brands/" + brandHouseId + "/messages"
}

/**
 * The integration messages for a brand: everything sent to connectors installed on the brandhouse
 * itself or on any of its shops. Administrators only - it exposes connector payloads and errors.
 */
export const BrandMessagesPage: FC<any> = () => {
   const {brandHouseId} = useParams<ParamBrandHouse>()
   const isAdministrator = useRecoilValue(CurrentUserIsAdmin);
   const [shopIds, setShopIds] = useState<string[]>([]);

   // Hierarchy hides the menu entry from non-administrators, but the route is guessable - so guard
   // here too. Simplified administrators are deliberately still allowed in.
   const denied = !isAdministrator;

   const {loading: shopsLoading} = useBrandMessagesShopsQuery({
      variables: {
         brandHouseId: brandHouseId!
      },
      skip: denied || !brandHouseId,
      onCompleted: (result) => {
         setShopIds(result.ShopMany?.map(shop => String(shop._id)) || []);
      }
   });

   // A brand owns the instances installed on the brandhouse plus those on each of its shops.
   const instanceFilter = useMemo((): FilterFindManyIntegrationInstanceInput => {
      return {
         OR: [{
            refId: brandHouseId,
            refType: EnumIntegrationInstanceRefType.BRANDHOUSE
         }, ...shopIds.map(id => ({
            refId: id,
            refType: EnumIntegrationInstanceRefType.SHOP
         }))]
      }
   }, [brandHouseId, shopIds]);

   const {data, loading: instancesLoading} = useBrandMessagesInstancesQuery({
      variables: {
         instanceFilter
      },
      skip: denied || !brandHouseId || shopsLoading
   });

   const instanceIds = useMemo(() => {
      return data?.IntegrationInstances?.map(instance => String(instance._id)) || []
   }, [data]);

   if (denied) {
      return <Page>
         <PageHeader title={"Messages"}/>
         <PageContent>
            <BodyText type={"body1"}>You do not have access to this page.</BodyText>
         </PageContent>
      </Page>
   }

   return <Page>
      <PageHeader title={"Messages sent to connectors"}/>
      {(shopsLoading || instancesLoading || !data) ?
         <LinearProgress/> :
         <MessagesList globalId={"brandMessagesList"} scopeInstanceIds={instanceIds}/>
      }
   </Page>
}
